/**
 * Data Controller — argus-backend slice of a personal-data export.
 *
 * `GET /api/notes/staff/data-controller/subject/{uuid}` returns every row in every D1 table that
 * references the subject (as the person the row is about, or as the acting staff member), plus
 * Analytics Engine vote events and which KV caches hold transient copies. The backend gate is
 * `argus.datacontroller` (or SPY_Super_admin); a 403 here means the caller has neither.
 *
 * Auth: Cloudflare Access session cookies (same as every other `/api/notes/*` call).
 */

const DATA_CONTROLLER_API_BASE_URL = 'https://spy.playcdu.co/api/notes/staff/data-controller';

export type BackendDatasetStatus = 'ok' | 'empty' | 'error' | 'skipped';
export type BackendDatasetRole = 'subject' | 'actor';
export type BackendDatasetCategory = 'identity' | 'gameplay' | 'economy' | 'moderation' | 'logs' | 'community' | 'staff' | 'system';

export interface BackendDatasetResult {
  id: string;
  table: string;
  title: string;
  description: string;
  category: BackendDatasetCategory;
  role: BackendDatasetRole;
  status: BackendDatasetStatus;
  error?: string;
  matched_on: string[];
  rows: Record<string, unknown>[];
  total: number;
  truncated: boolean;
  duration_ms: number;
}

export interface BackendSubjectIds {
  uuid: string;
  uuidCompact: string;
  discordId: string | null;
  email: string | null;
  username: string | null;
  tesseractId: string | null;
}

export interface BackendSubjectExport {
  generated_at: number;
  subject: BackendSubjectIds;
  datasets: BackendDatasetResult[];
  non_personal_tables: { table: string; reason: string }[];
  totals: { datasets: number; with_data: number; errors: number; skipped: number; rows: number };
}

export interface BackendCatalogue {
  generated_at: number;
  datasets: {
    id: string;
    table: string;
    title: string;
    description: string;
    category: BackendDatasetCategory;
    role: BackendDatasetRole;
    external: boolean;
    match: { column: string; kind: string; op?: string }[] | null;
    custom_query: boolean;
  }[];
  non_personal_tables: { table: string; reason: string }[];
}

export interface SubjectHints {
  discord_id?: string | null;
  email?: string | null;
  username?: string | null;
  tesseract_id?: string | null;
}

export class DataControllerApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'DataControllerApiError';
    this.status = status;
  }
}

/** `running`: the server-side run has started and cluster session logs are still being scanned, one batch per run call. */
export type ErasureStatus = 'awaiting_confirmation' | 'confirmed' | 'overridden' | 'running' | 'completed' | 'cancelled';
export type ErasureAction = 'deleted' | 'de-identified' | 'retained' | 'nothing' | 'failed' | 'manual';

export interface ErasureOutcome {
  system: string;
  step: string;
  action: ErasureAction;
  count?: number;
  detail?: string;
}

export interface ErasureResult {
  argus: ErasureOutcome[] | null;
  tesseract: ErasureOutcome[] | null;
  /** The Redis steps, run by the backend over the game tunnel. */
  redis?: ErasureOutcome[] | null;
  client: ErasureOutcome[] | null;
  completion_email_sent?: boolean;
}

/** One deletion request as the backend records it (`data_erasures`). */
export interface ErasureRecord {
  id: string;
  reference: string;
  status: ErasureStatus;
  subject: {
    uuid: string;
    username: string | null;
    discordId: string | null;
    tesseractId: string | null;
    tesseractUsername: string | null;
    emailMasked: string | null;
    /** How Tesseract found the website account: by the Minecraft link, or by an identifier staff supplied. */
    accountMatchedBy?: 'user_id' | 'minecraft' | 'email' | 'username' | null;
    /** The account has no Minecraft link; staff named it and the player together, so it is treated as theirs. */
    assumedLink?: boolean;
  } | null;
  /** A Tesseract (website) account matched the player, so a confirmation email could be sent. */
  has_account: boolean;
  holder_email_masked: string | null;
  expires_at: number | null;
  expired: boolean;
  confirmation: 'email' | 'override' | null;
  override_reason: string | null;
  requested_by: string;
  requested_at: number;
  confirmed_at: number | null;
  overridden_by: string | null;
  overridden_at: number | null;
  cancelled_by: string | null;
  cancelled_at: number | null;
  executed_by: string | null;
  executed_at: number | null;
  completed_at: number | null;
  result: ErasureResult;
  /** Clusters whose session logs are still to be scanned; the run continues one batch per run call while non-empty. */
  redis_pending: string[];
  client_steps_pending: boolean;
  /** A Redis step failed or could not run (game Redis not configured); staff finish it by hand. */
  redis_needs_attention?: boolean;
}

export interface ErasureHints extends SubjectHints {
  tesseract_username?: string | null;
}

export interface TesseractDataset {
  id: string;
  title: string;
  description: string;
  rows: Record<string, unknown>[];
  total: number;
  truncated: boolean;
  error?: string;
}

/** Everything Tesseract holds about one person (backend proxy of Tesseract's data-subject export). */
export interface TesseractSubjectExport {
  generated_at: number;
  matched_by: 'user_id' | 'minecraft' | 'email' | 'username' | 'discord' | null;
  account: Record<string, unknown> | null;
  minecraft: Record<string, unknown> | null;
  discord: Record<string, unknown> | null;
  datasets: TesseractDataset[];
  totals: { datasets: number; with_data: number; rows: number; errors: number; skipped: number };
}

class DataControllerApiService {
  private async request<T>(endpoint: string, init: { method?: 'GET' | 'POST'; body?: unknown } = {}): Promise<T> {
    const response = await fetch(`${DATA_CONTROLLER_API_BASE_URL}${endpoint}`, {
      method: init.method ?? 'GET',
      cache: 'no-store',
      ...(init.body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(init.body) } : {}),
      // Cloudflare Access handles authentication via cookies
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new DataControllerApiError(response.status, describeStatus(response.status, body?.error));
    }
    return response.json();
  }

  /** The website account and every Tesseract dataset for the player, matched by Minecraft link, Discord ID, email or website username. Never cached. */
  getTesseractSubject(uuid: string, hints: ErasureHints = {}): Promise<TesseractSubjectExport> {
    const params = new URLSearchParams();
    if (hints.discord_id) params.set('discord_id', hints.discord_id);
    if (hints.email) params.set('email', hints.email);
    if (hints.username) params.set('username', hints.username);
    if (hints.tesseract_id) params.set('tesseract_id', hints.tesseract_id);
    if (hints.tesseract_username) params.set('tesseract_username', hints.tesseract_username);
    const qs = params.toString();
    return this.request<TesseractSubjectExport>(`/subject/${encodeURIComponent(uuid)}/tesseract${qs ? `?${qs}` : ''}`);
  }

  // --- Delete and de-identify -------------------------------------------------------------------

  /** The latest deletion request for the player, or null. Refreshed against Tesseract while it waits on the holder. */
  async getErasure(uuid: string): Promise<ErasureRecord | null> {
    const { erasure } = await this.request<{ erasure: ErasureRecord | null }>(`/subject/${encodeURIComponent(uuid)}/erasure`);
    return erasure;
  }

  /** Opens a request: Tesseract emails the account holder a confirmation link. Returns the existing open request when there is one. */
  requestErasure(uuid: string, hints: ErasureHints = {}): Promise<{ erasure: ErasureRecord; reused: boolean; email_sent?: boolean | null }> {
    const body: Record<string, string> = {};
    if (hints.discord_id) body.discord_id = hints.discord_id;
    if (hints.email) body.email = hints.email;
    if (hints.username) body.username = hints.username;
    if (hints.tesseract_id) body.tesseract_id = hints.tesseract_id;
    if (hints.tesseract_username) body.tesseract_username = hints.tesseract_username;
    return this.request(`/subject/${encodeURIComponent(uuid)}/erasure`, { method: 'POST', body });
  }

  async overrideErasure(id: string, reason: string): Promise<ErasureRecord> {
    return (await this.request<{ erasure: ErasureRecord }>(`/erasure/${encodeURIComponent(id)}/override`, { method: 'POST', body: { reason } })).erasure;
  }

  async cancelErasure(id: string): Promise<ErasureRecord> {
    return (await this.request<{ erasure: ErasureRecord }>(`/erasure/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} })).erasure;
  }

  /** Server-side run: the backend's own rows, then the Tesseract account. */
  async runErasure(id: string): Promise<ErasureRecord> {
    return (await this.request<{ erasure: ErasureRecord }>(`/erasure/${encodeURIComponent(id)}/run`, { method: 'POST', body: {} })).erasure;
  }

  /** Outcomes of the steps the browser ran (Redis, Elasticsearch). */
  async reportErasureClientSteps(id: string, steps: ErasureOutcome[]): Promise<ErasureRecord> {
    return (await this.request<{ erasure: ErasureRecord }>(`/erasure/${encodeURIComponent(id)}/client-steps`, { method: 'POST', body: { steps } })).erasure;
  }

  getCatalogue(): Promise<BackendCatalogue> {
    return this.request<BackendCatalogue>('/catalogue');
  }

  /** Every D1 / KV / Analytics Engine record the backend holds on the subject. Never cached. */
  exportSubject(uuid: string, hints: SubjectHints = {}): Promise<BackendSubjectExport> {
    const params = new URLSearchParams();
    if (hints.discord_id) params.set('discord_id', hints.discord_id);
    if (hints.email) params.set('email', hints.email);
    if (hints.username) params.set('username', hints.username);
    if (hints.tesseract_id) params.set('tesseract_id', hints.tesseract_id);
    const qs = params.toString();
    return this.request<BackendSubjectExport>(`/subject/${encodeURIComponent(uuid)}${qs ? `?${qs}` : ''}`);
  }
}

function describeStatus(status: number, serverMessage?: string): string {
  switch (status) {
    case 400:
      return serverMessage || 'The subject identifiers were rejected as invalid.';
    case 403:
      return serverMessage || 'The Data Controller requires the argus.datacontroller permission.';
    case 409:
      return serverMessage || 'The request is not in a state that allows this.';
    case 500:
      return serverMessage || 'The backend export failed.';
    default:
      return serverMessage || `API Error: ${status}`;
  }
}

export const dataControllerApiService = new DataControllerApiService();
